import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FiCopy, FiCheck, FiEdit3, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import styles from './LiveEditor.module.scss';

const LiveEditor = ({
  streamContent,
  isGenerating,
  contentGenerated,
  onSaveToDocument,
  onReviewInEditor,
  onContentChange,
  currentVersion = 1,
  totalVersions = 1,
  onVersionChange,
}) => {
  const contentRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Auto-scroll to bottom as content streams in
  useEffect(() => {
    if (contentRef.current && isGenerating) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [streamContent, isGenerating]);

  const handleCopy = () => {
    const textContent = contentRef.current?.innerText || streamContent.replace(/<[^>]*>/g, '');
    navigator.clipboard.writeText(textContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleContentEdit = (e) => {
    // Only save on blur to prevent cursor jumping
    if (onContentChange && !isGenerating) {
      const newContent = e.currentTarget.innerHTML;
      onContentChange(newContent);
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
  };

  const handleBlur = (e) => {
    setIsEditing(false);
    // Save changes when user finishes editing
    handleContentEdit(e);
  };

  return (
    <div className={styles.live_editor}>
      {/* Output Card - matching questionnaire style */}
      <div className={styles.output_card}>
        <div className={styles.output_header}>
          <div className={styles.header_left}>
            <h4 className={styles.output_title}>
              {isGenerating && <span className={styles.pulse_dot}></span>}
              {isGenerating ? 'Generating...' : 'Document'}
            </h4>
            {contentGenerated && totalVersions > 0 && (
              <div className={styles.version_selector}>
                <button
                  className={styles.version_nav_btn}
                  onClick={() => onVersionChange?.(currentVersion - 1)}
                  disabled={currentVersion <= 1 || isGenerating}
                  title="Previous version"
                >
                  <FiChevronLeft size={14} />
                </button>
                <span className={styles.version_badge}>v{currentVersion} of {totalVersions}</span>
                <button
                  className={styles.version_nav_btn}
                  onClick={() => onVersionChange?.(currentVersion + 1)}
                  disabled={currentVersion >= totalVersions || isGenerating}
                  title="Next version"
                >
                  <FiChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
          {contentGenerated && (
            <div className={styles.header_actions}>
              <Button
                size="sm"
                variant=""
                className={styles.action_button}
                onClick={handleCopy}
                title={copied ? "Copied!" : "Copy to clipboard"}
              >
                {copied ? <FiCheck size={13} /> : <FiCopy size={13} />}
              </Button>
              <Button
                size="sm"
                variant=""
                className={styles.action_button}
                onClick={onReviewInEditor}
                title="Review in Editor"
              >
                <FiEdit3 size={13} />
              </Button>
              <Button
                size="sm"
                variant=""
                className={styles.primary_button}
                onClick={onSaveToDocument}
                title="Save to Library"
              >
                Save
              </Button>
            </div>
          )}
        </div>

        <div className={styles.output_display}>
          <div className={styles.editor_content} ref={contentRef}>
            {!streamContent && !isGenerating && (
              <div className={styles.empty_state}>
                <FiDownload size={48} color="#00a7b7" opacity={0.3} />
                <p>Fill in the form and click "Run Prompt" to generate content</p>
              </div>
            )}
            {streamContent && (
              <div
                className={`${styles.content_display} ${isEditing ? styles.editing : ''}`}
                contentEditable={!isGenerating && contentGenerated}
                suppressContentEditableWarning={true}
                onFocus={handleFocus}
                onBlur={handleBlur}
                dangerouslySetInnerHTML={{ __html: streamContent }}
              />
            )}
            {isGenerating && !streamContent && (
              <div className={styles.loading_state}>
                <div className={styles.loading_spinner}></div>
                <p>Preparing your content...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveEditor;
